// src/services/reportBuilder.js
import templatePt from "../data/pt/character_templates.json";
import templateEn from "../data/en/character_templates.json";

// Dependências: computeInsights (do profileHelpers), archetypes map (se tiver)
import { computeInsights } from "../utils/profileHelpers"; // ajuste path conforme seu projeto

// map character key -> ACT process short label
const CHAR_TO_ACT_PROCESS = {
  emotion: { process: "Aceitação", focus: "aceitação" },
  discipline: { process: "Ação de Compromisso", focus: "ação" },
  mind: { process: "Desfusão", focus: "desfusão" },
  perspective: { process: "Self & Presença", focus: "presença" },
};

function make30DayPlan(chosenCharKey) {
  // construímos um plano em 4 semanas adaptado ao temperamento (resumo básico, extensível)
  const weeks = [];

  // week templates by process (short, will be expanded)
  if (chosenCharKey === "emotion") {
    weeks.push({
      title: "Semana 1 — Aceitação e contato com sensações",
      bullets: [
        "Prática diária de 2 min de notar sensações sem tentar mudar.",
        "Nomear emoções em 1 palavra ao longo do dia (3x).",
        "Mini-exposição: aceitar 60s de desconforto sem reagir.",
      ],
    });
    weeks.push({
      title: "Semana 2 — Aceitação aplicada à ação",
      bullets: [
        "Identificar 1 valor e executar 1 ação curta alinhada (≤10min).",
        "Reparar quando evitou e descrever a sensação sem culpa.",
      ],
    });
    weeks.push({
      title: "Semana 3 — Relacionamentos e limites",
      bullets: [
        "Prática de partilha curta (30–60s) com pessoa de confiança.",
        "Registre 1 momento em que aceitou antes de responder.",
      ],
    });
    weeks.push({
      title: "Semana 4 — Consolidação",
      bullets: [
        "Revisar progresso; escolha 2 práticas para manter mensalmente.",
        "Plano de ação: como reagir a recaídas (3 passos).",
      ],
    });
  } else if (chosenCharKey === "discipline") {
    weeks.push({
      title: "Semana 1 — Micro-hábitos",
      bullets: [
        "Defina 1 rotina de 2 min (ex.: abrir caderno e anotar 3 tarefas).",
        "Use alarme como gatilho e execute por 7 dias seguidos.",
      ],
    });
    weeks.push({
      title: "Semana 2 — Valores como motor",
      bullets: [
        "Escreva por 5 minutos o porquê da rotina (valor).",
        "Assine um compromisso curto com alguém (accountability).",
      ],
    });
    weeks.push({
      title: "Semana 3 — Sustentação",
      bullets: [
        "Transforme uma micro-tarefa em bloco de 15 min (Pomodoro).",
        "Ajuste rotinas com base no desgaste percebido.",
      ],
    });
    weeks.push({
      title: "Semana 4 — Autonomia",
      bullets: [
        "Delegue/automatize 1 rotina e planeje próximo mês.",
        "Consolide com um ritual semanal de revisão (10 min).",
      ],
    });
  } else if (chosenCharKey === "mind") {
    weeks.push({
      title: "Semana 1 — Desfusão básica",
      bullets: [
        "Exercício diário: repetir pensamento com voz neutra 1x (2 min).",
        "Nomeação de pensamentos: 'Estou tendo o pensamento X' (3x/dia).",
      ],
    });
    weeks.push({
      title: "Semana 2 — Perspectiva e testes",
      bullets: [
        "Anote 2 interpretações alternativas para um pensamento recorrente.",
        "Experimente agir contrariamente ao pensamento por 5 min.",
      ],
    });
    weeks.push({
      title: "Semana 3 — Desfusão em relações",
      bullets: [
        "Antes de reagir numa conversa, faça 20s de respiração.",
        "Prática de 'voz absurda' para reduzir carga verbal.",
      ],
    });
    weeks.push({
      title: "Semana 4 — Consolidação",
      bullets: [
        "Integre 1 técnica de desfusão à rotina diária.",
        "Planeje quando usar cada técnica em situações específicas.",
      ],
    });
  } else {
    // perspective
    weeks.push({
      title: "Semana 1 — Observador e distância",
      bullets: [
        "Prática curta de 'eu como contexto' (2–3 min).",
        "Exercício: escrever a própria experiência em 3ª pessoa.",
      ],
    });
    weeks.push({
      title: "Semana 2 — Grounding e tomada de perspectiva",
      bullets: [
        "Antes de decisão, 'avisar' a si mesmo: 'vou observar por 60s'.",
        "Registrar momentos em que se sentiu identificado e analisar.",
      ],
    });
    weeks.push({
      title: "Semana 3 — Ação com presença",
      bullets: [
        "Fazer 1 ação intencional usando checkpoint de 20s.",
        "Prática de 3 minutos de foco na respiração antes de reuniões.",
      ],
    });
    weeks.push({
      title: "Semana 4 — Consolidação",
      bullets: [
        "Mapear gatilhos e respostas automáticas e criar planos 'if → then'.",
        "Escolher 2 práticas a manter mensalmente.",
      ],
    });
  }

  return weeks;
}

export function buildPremiumPayload({
  traitsArray, // [E, A, S, discipline, mentalOpenness, emotionalOpenness, perspective?]
  archetypeOverride, // e.g. { label, key, description, image }
  user, // { name, email, id }
  lang,
}) {
  // compute insights
  const insights = computeInsights(traitsArray, archetypeOverride);

  const template = lang?.startsWith("pt") ? templatePt : templateEn;

  // normalize temperament object
  const temperament = {
    Emocionalidade:
      insights.temperRows.find((r) => r.label === "Emocionalidade")?.value ||
      traitsArray[0],
    Atividade:
      insights.temperRows.find((r) => r.label === "Atividade")?.value ||
      traitsArray[1],
    Sociabilidade:
      insights.temperRows.find((r) => r.label === "Sociabilidade")?.value ||
      traitsArray[2],
  };

  // chosen character info
  const chosen = insights.chosenCharacter || {
    key: "emotion",
    label: "Abertura Emocional",
    value: traitsArray[5] || 50,
  };

  // map to ACT process
  const actMap = CHAR_TO_ACT_PROCESS[chosen.key] || {
    process: "Aceitação/ação",
    focus: "geral",
  };

  // create actExercises (3 exercises) derived from chosen key
  const actExercises = [];

  actExercises.push({
    title: template[chosen.key].exercises[0].title,
    description: template[chosen.key].exercises[0].description,
  });
  actExercises.push({
    title: template[chosen.key].exercises[1].title,
    description: template[chosen.key].exercises[1].description,
  });
  actExercises.push({
    title: template[chosen.key].exercises[2].title,
    description: template[chosen.key].exercises[2].description,
  });

  // build development_map_summary (sintético)
  const development_map_summary = `Foco principal: ${
    chosen.label
  } — processo ACT: ${
    actMap.process
  }. Ao integrar seu temperamento (${Object.entries(temperament)
    .map(([k, v]) => `${k}: ${v}%`)
    .join(" · ")}) o plano prioriza ${
    actMap.focus
  } e inclui rotinas semanais para consolidar mudanças.`;

  const miniPlan = insights.miniPlan; // já provido por computeInsights (7 dias)
  const thirtyDay = make30DayPlan(
    chosen.key,
    temperament,
    archetypeOverride?.label || insights.archetype.label
  );

  const payload = {
    name: user?.name || "Usuário",
    email: user?.email || "",
    id: user?.id || "",
    date: new Date().toLocaleDateString(),
    archetypeKey: archetypeOverride?.key || "—",
    archetype_label: archetypeOverride?.label || "—",
    archetype_image:
      archetypeOverride?.image || insights.archetype?.archetype_image || "",
    clanKey: archetypeOverride?.clan || insights.archetype?.clanKey || "tide", // class for css
    personalityType: insights.archetype.label || "—",
    personalityTraits: traitsArray.slice(0, 6).map((v) => Math.round(v || 0)),
    characterTraits: {
      discipline: Math.round(traitsArray[3] || 0),
      mentalOpenness: Math.round(traitsArray[4] || 0),
      emotionalOpenness: Math.round(traitsArray[5] || 0),
      perspective: Math.round(traitsArray[6] || 50),
    },
    temperamentStrengths: insights.temperamentStrengths,
    characterResources: insights.characterResources,
    integrationMatrix: insights.integrationMatrix,
    recommendations: insights.recommendations,
    miniPlan,
    thirtyDayPlan: thirtyDay,
    actExercises,
    development_map_summary,
    chosenCharacter: chosen,

    // convenient short summary used in header
    summary_executive:
      insights.archetype.description ||
      `${insights.archetype.label} — perfil personalizado.`,
  };

  return payload;
}
